import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomers } from '../store/slices/customersSlice';
import './InvoiceModal.css';

const getCustomerId = (customer) => customer?._id || customer?.id;

const matchesCustomerSearch = (customer, query) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  return (
    customer.name?.toLowerCase().includes(normalized) ||
    (customer.phoneNumber || customer.phone || '').includes(query.trim()) ||
    customer.email?.toLowerCase().includes(normalized)
  );
};

const InvoiceModal = ({ isOpen, onClose, onSave, invoice = null, products = [] }) => {
  const dispatch = useDispatch();
  const { customers, loading: customersLoading } = useSelector(state => state.customers);
  const [formData, setFormData] = useState({
    customerId: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [],
    subtotal: 0,
    igstRate: 0,
    igst: 0,
    cgstRate: 0,
    cgst: 0,
    sgstRate: 0,
    sgst: 0,
    totalTax: 0,
    discount: 0,
    discountType: 'amount',
    total: 0,
    currency: 'INR',
    notes: '',
    taxType: 'igst' // 'igst' or 'split' (for cgst+sgst)
  });
  const [errors, setErrors] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const isEditMode = !!invoice;

  const filteredCustomers = useMemo(
    () => (customers || []).filter(customer => matchesCustomerSearch(customer, customerSearch)),
    [customers, customerSearch]
  );

  // Load customers when modal opens (same pattern as BroadcastModal)
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCustomers({ page: 1, limit: 500 }))
        .unwrap()
        .catch(err => console.error('Failed to fetch customers:', err));
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (invoice) {
      setFormData({
        customerId: invoice.customerId || '',
        date: invoice.invoiceDate
          ? new Date(invoice.invoiceDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        dueDate: invoice.dueDate
          ? new Date(invoice.dueDate).toISOString().split('T')[0]
          : '',
        items: (invoice.items || []).map(item => {
          // Try to find matching product by name
          const matchingProduct = products.find(
            p => p.name === item.description || String(p.id) === String(item.productId)
          );
          return {
            productId: matchingProduct ? String(matchingProduct.id) : '',
            name: item.description || '',
            hsn: item.hsn || '',
            quantity: item.quantity || 1,
            price: item.unitPrice || 0,
            total: item.total || 0,
            isService: !matchingProduct // Mark as service if product not found
          };
        }),
        subtotal: invoice.subtotal || 0,
        igstRate: invoice.igstRate || 0,
        igst: invoice.igst || 0,
        cgstRate: invoice.cgstRate || 0,
        cgst: invoice.cgst || 0,
        sgstRate: invoice.sgstRate || 0,
        sgst: invoice.sgst || 0,
        totalTax: invoice.totalTax || invoice.tax || 0,
        discount: invoice.discountType === 'percentage' ? (invoice.discountRate || 0) : (invoice.discount || 0),
        discountType: invoice.discountType || 'amount',
        total: invoice.total || 0,
        currency: invoice.currency || 'INR',
        notes: invoice.notes || '',
        taxType: (invoice.cgstRate || invoice.sgstRate) ? 'split' : 'igst'
      });
    } else {
      const today = new Date();
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + 30);
      
      setFormData({
        customerId: '',
        date: today.toISOString().split('T')[0],
        dueDate: dueDate.toISOString().split('T')[0],
        items: [],
        subtotal: 0,
        igstRate: 0,
        igst: 0,
        cgstRate: 0,
        cgst: 0,
        sgstRate: 0,
        sgst: 0,
        totalTax: 0,
        discount: 0,
        discountType: 'amount',
        total: 0,
        currency: 'INR',
        notes: '',
        taxType: 'igst'
      });
    }
    setErrors({});
    setCustomerSearch('');
    setIsCustomerDropdownOpen(false);

    if (invoice) {
      const customerId = invoice.customerId || '';
      const matched = (customers || []).find(c => getCustomerId(c) === customerId);
      if (matched) {
        setSelectedCustomer(matched);
      } else if (invoice.billTo) {
        setSelectedCustomer({
          _id: customerId,
          name: invoice.billTo.name,
          phoneNumber: invoice.billTo.phone,
          email: invoice.billTo.email,
        });
      } else {
        setSelectedCustomer(null);
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [invoice, isOpen, customers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isCustomerDropdownOpen && !e.target.closest('.searchable-select')) {
        setIsCustomerDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCustomerDropdownOpen]);

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    if (!isCustomerDropdownOpen) {
      setIsCustomerDropdownOpen(true);
    }
  };

  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({ ...prev, customerId: getCustomerId(customer) }));
    setSelectedCustomer(customer);
    setIsCustomerDropdownOpen(false);
    setCustomerSearch('');
    if (errors.customerId) {
      setErrors(prev => ({ ...prev, customerId: '' }));
    }
  };

  const calculateTotals = (items, igstRate, cgstRate, sgstRate, taxType, discount, discountType) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    let discountAmount = 0;
    
    if (discountType === 'percentage') {
      discountAmount = (subtotal * discount) / 100;
    } else {
      discountAmount = discount;
    }
    
    const afterDiscount = subtotal - discountAmount;
    let taxAmount = 0;
    let igstAmount = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    
    if (taxType === 'igst') {
      igstAmount = (afterDiscount * igstRate) / 100;
      taxAmount = igstAmount;
    } else if (taxType === 'split') {
      cgstAmount = (afterDiscount * cgstRate) / 100;
      sgstAmount = (afterDiscount * sgstRate) / 100;
      taxAmount = cgstAmount + sgstAmount;
    }
    
    const total = afterDiscount + taxAmount;

    return { subtotal, discountAmount, igstAmount, cgstAmount, sgstAmount, totalTax: taxAmount, total };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    
    if (name === 'igstRate' || name === 'cgstRate' || name === 'sgstRate' || name === 'discount') {
      newValue = parseFloat(value) || 0;
    }

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      
      if (name === 'igstRate' || name === 'cgstRate' || name === 'sgstRate' || name === 'discount' || name === 'discountType' || name === 'taxType') {
        const totals = calculateTotals(updated.items, updated.igstRate, updated.cgstRate, updated.sgstRate, updated.taxType, updated.discount, updated.discountType);
        updated.subtotal = totals.subtotal;
        updated.igst = totals.igstAmount;
        updated.cgst = totals.cgstAmount;
        updated.sgst = totals.sgstAmount;
        updated.totalTax = totals.totalTax;
        updated.total = totals.total;
      }
      
      return updated;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', name: '', hsn: '', quantity: 1, price: 0, total: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      const totals = calculateTotals(newItems, prev.igstRate, prev.cgstRate, prev.sgstRate, prev.taxType, prev.discount, prev.discountType);
      return {
        ...prev,
        items: newItems,
        subtotal: totals.subtotal,
        igst: totals.igstAmount,
        cgst: totals.cgstAmount,
        sgst: totals.sgstAmount,
        totalTax: totals.totalTax,
        total: totals.total
      };
    });
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index] };
      
      if (field === 'productId') {
        const product = products.find(p => String(p.id) === String(value));
        if (product) {
          item.productId = product.id;
          item.name = product.name;
          item.price = product.price;
          item.total = item.quantity * product.price;
        }
      } else if (field === 'quantity' || field === 'price') {
        item[field] = parseFloat(value) || 0;
        item.total = item.quantity * item.price;
      } else {
        item[field] = value;
      }
      
      newItems[index] = item;
      const totals = calculateTotals(newItems, prev.igstRate, prev.cgstRate, prev.sgstRate, prev.taxType, prev.discount, prev.discountType);
      
      return {
        ...prev,
        items: newItems,
        subtotal: totals.subtotal,
        igst: totals.igstAmount,
        cgst: totals.cgstAmount,
        sgst: totals.sgstAmount,
        totalTax: totals.totalTax,
        total: totals.total
      };
    });
  };

  const handleAddService = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', name: '', hsn: '', quantity: 1, price: 0, total: 0, isService: true }]
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }

    if (!formData.date) {
      newErrors.date = 'Invoice date is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      if (!item.name || !item.name.trim()) {
        newErrors[`item_${index}_name`] = 'Item name is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (!item.price || item.price <= 0) {
        newErrors[`item_${index}_price`] = 'Price must be greater than 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const totals = calculateTotals(formData.items, formData.igstRate, formData.cgstRate, formData.sgstRate, formData.taxType, formData.discount, formData.discountType);
      const mappedItems = formData.items.map(item => ({
        description: item.name,
        hsn: item.hsn || '',
        quantity: item.quantity,
        unitPrice: item.price,
        total: item.total
      }));
      onSave({
        customerId: formData.customerId,
        invoiceDate: formData.date,
        dueDate: formData.dueDate,
        items: mappedItems,
        subtotal: totals.subtotal,
        igstRate: formData.igstRate,
        igst: formData.igst,
        cgstRate: formData.cgstRate,
        cgst: formData.cgst,
        sgstRate: formData.sgstRate,
        sgst: formData.sgst,
        tax: totals.totalTax,
        discountType: formData.discountType,
        discountRate: formData.discountType === 'percentage' ? formData.discount : 0,
        discount: totals.discountAmount,
        total: totals.total,
        currency: formData.currency || 'INR',
        notes: formData.notes
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invoice-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Invoice' : 'Create New Invoice'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerId">Customer *</label>
              <div className="searchable-select" style={{ position: 'relative' }}>
                <div className="search-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="customerId"
                    placeholder="Search by name, phone, or email..."
                    value={customerSearch}
                    onChange={(e) => handleCustomerSearchChange(e.target.value)}
                    onFocus={() => setIsCustomerDropdownOpen(true)}
                    className={`form-select search-input ${errors.customerId ? 'error' : ''}`}
                    style={{ paddingRight: '30px' }}
                  />
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {isCustomerDropdownOpen && (
                  <div className="dropdown-menu customer-dropdown">
                    {customersLoading ? (
                      <div className="customer-dropdown-message">Loading customers...</div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="customer-dropdown-message">
                        {customerSearch.trim()
                          ? `No customers found matching "${customerSearch.trim()}"`
                          : 'No customers available'}
                      </div>
                    ) : (
                      filteredCustomers.map(customer => {
                        const customerId = getCustomerId(customer);
                        const isSelected = formData.customerId === customerId;
                        return (
                          <div
                            key={customerId}
                            className={`customer-dropdown-item${isSelected ? ' selected' : ''}`}
                            onClick={() => handleSelectCustomer(customer)}
                          >
                            <div className="customer-dropdown-name">{customer.name}</div>
                            <div className="customer-dropdown-meta">
                              {customer.phoneNumber || customer.phone || 'N/A'} • {customer.email || 'N/A'}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="selected-customer-chip">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <div>
                      <div className="selected-customer-label">Selected Customer</div>
                      <div className="selected-customer-name">{selectedCustomer.name}</div>
                      {(selectedCustomer.phoneNumber || selectedCustomer.phone || selectedCustomer.email) && (
                        <div className="selected-customer-meta">
                          {[selectedCustomer.phoneNumber || selectedCustomer.phone, selectedCustomer.email]
                            .filter(Boolean)
                            .join(' • ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.customerId && <span className="error-message">{errors.customerId}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="date">Invoice Date *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={errors.date ? 'error' : ''}
              />
              {errors.date && <span className="error-message">{errors.date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="dueDate">Due Date *</label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                min={formData.date}
                className={errors.dueDate ? 'error' : ''}
              />
              {errors.dueDate && <span className="error-message">{errors.dueDate}</span>}
            </div>
          </div>

          <div className="section-divider">
            <h3>Invoice Items</h3>
            <div className="section-actions">
              <button type="button" className="btn-add-item" onClick={handleAddItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Product
              </button>
              <button type="button" className="btn-add-service" onClick={handleAddService}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Service
              </button>
            </div>
          </div>

          {errors.items && <span className="error-message">{errors.items}</span>}

          <div className="invoice-items-list">
            {formData.items.map((item, index) => (
              <div key={index} className="invoice-item-row">
                <div className="item-product">
                  {!item.isService ? (
                    <select
                      value={item.productId || ''}
                      onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={String(product.id)} value={String(product.id)}>
                          {product.name} - {formatCurrency(product.price)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder="Service name"
                      value={item.name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      className={errors[`item_${index}_name`] ? 'error' : ''}
                    />
                  )}
                  {!item.isService && item.productId && (
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      className={errors[`item_${index}_name`] ? 'error' : ''}
                    />
                  )}
                  {errors[`item_${index}_name`] && (
                    <span className="error-message">{errors[`item_${index}_name`]}</span>
                  )}
                </div>
                <div className="item-hsn">
                  <input
                    type="text"
                    placeholder="HSN / SAC"
                    value={item.hsn || ''}
                    onChange={(e) => handleItemChange(index, 'hsn', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="item-quantity">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    min="0.01"
                    step="0.01"
                    className={errors[`item_${index}_quantity`] ? 'error' : ''}
                  />
                  {errors[`item_${index}_quantity`] && (
                    <span className="error-message">{errors[`item_${index}_quantity`]}</span>
                  )}
                </div>
                <div className="item-price">
                  <input
                    type="number"
                    placeholder="Price"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                    min="0"
                    step="0.01"
                    className={errors[`item_${index}_price`] ? 'error' : ''}
                  />
                  {errors[`item_${index}_price`] && (
                    <span className="error-message">{errors[`item_${index}_price`]}</span>
                  )}
                </div>
                <div className="item-total">
                  {formatCurrency(item.total || 0)}
                </div>
                <button
                  type="button"
                  className="btn-remove-item"
                  onClick={() => handleRemoveItem(index)}
                  title="Remove Item"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <div className="invoice-totals">
            <div className="totals-row">
              <span>Subtotal:</span>
              <strong>{formatCurrency(formData.subtotal)}</strong>
            </div>
            <div className="totals-row">
              <div className="discount-input-group">
                <span>Discount:</span>
                <div className="discount-controls">
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="discount-input"
                  />
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleChange}
                    className="discount-type-select"
                  >
                    <option value="amount">₹</option>
                    <option value="percentage">%</option>
                  </select>
                </div>
              </div>
              <strong>
                -{formatCurrency(
                  formData.discountType === 'percentage' 
                    ? (formData.subtotal * formData.discount) / 100 
                    : formData.discount
                )}
              </strong>
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <div className="tax-type-selector" style={{ marginBottom: '12px', display: 'flex', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="taxType"
                    value="igst"
                    checked={formData.taxType === 'igst'}
                    onChange={handleChange}
                  />
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>IGST</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="taxType"
                    value="split"
                    checked={formData.taxType === 'split'}
                    onChange={handleChange}
                  />
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>CGST + SGST</span>
                </label>
              </div>
              
              {formData.taxType === 'igst' ? (
                <div className="totals-row">
                  <div className="tax-input-group">
                    <span>IGST (%):</span>
                    <input
                      type="number"
                      name="igstRate"
                      value={formData.igstRate}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.01"
                      className="tax-rate-input"
                    />
                  </div>
                  <strong>{formatCurrency((formData.subtotal - (formData.discountType === 'percentage' ? (formData.subtotal * formData.discount) / 100 : formData.discount)) * formData.igstRate / 100)}</strong>
                </div>
              ) : (
                <>
                  <div className="totals-row">
                    <div className="tax-input-group">
                      <span>CGST (%):</span>
                      <input
                        type="number"
                        name="cgstRate"
                        value={formData.cgstRate}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="tax-rate-input"
                      />
                    </div>
                    <strong>{formatCurrency((formData.subtotal - (formData.discountType === 'percentage' ? (formData.subtotal * formData.discount) / 100 : formData.discount)) * formData.cgstRate / 100)}</strong>
                  </div>
                  <div className="totals-row">
                    <div className="tax-input-group">
                      <span>SGST (%):</span>
                      <input
                        type="number"
                        name="sgstRate"
                        value={formData.sgstRate}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        step="0.01"
                        className="tax-rate-input"
                      />
                    </div>
                    <strong>{formatCurrency((formData.subtotal - (formData.discountType === 'percentage' ? (formData.subtotal * formData.discount) / 100 : formData.discount)) * formData.sgstRate / 100)}</strong>
                  </div>
                </>
              )}
            </div>
            <div className="totals-row">
              <span style={{ fontSize: '13px', color: '#666' }}>Total Tax:</span>
              <strong style={{ fontSize: '13px' }}>{formatCurrency(formData.totalTax)}</strong>
            </div>
            <div className="totals-row total-row">
              <span>Total:</span>
              <strong className="total-amount">{formatCurrency(formData.total)}</strong>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes or terms..."
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? 'Update Invoice' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceModal;

